import React, {useEffect, useState} from "react";
import {
    BiddingView,
    BidViewItem,
    NameViewMode,
    AmountViewMode,
    BidderViewMode, BidSummaryModel,
} from "../bid/bidding_model";
import WhereYouStandDiagram from "./WhereYouStandDiagram";
import {USDollar, USDollarCompact} from "../../api/utils";
import {Link, useParams} from "react-router-dom";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "../ui/card";
import {Badge} from "../ui/badge";
import {moneyFormatter} from "../../lib/formatters";
import {Eligibility, RoundModel} from "../project_conf/round_model";
import {Button} from "../ui/button";
import BaseLoadingSpinner from "../ui/baseLoadingSpinner";
import {fetchWithToken} from "../../api/apiBase";
import {useAuthInfo} from "@propelauth/react";
import CreateBidButton from "../navButtons/CreateBidButton";


type BidFeedbackProps = {
    bidViewData: BiddingView;
    showTitle?: boolean;
}

export function getOrdinal(n: number) {
    if (n % 10 == 1 && n % 100 != 11) {
        return 'st';
    } else if (n % 10 == 2 && n % 100 != 12) {
        return 'nd';
    } else if (n % 10 == 3 && n % 100 != 13) {
        return 'rd';
    }
    return 'th';
}
const AmountViewLabel: Record<string, string> = {
    'visible': 'Visible',
    'ranked': 'Ranked Only',
    'hidden': 'Hidden',
    'diagram': 'Shown as relative positions.',
    'lowmidhigh': 'Low / Mid / High',
};

const NameViewLabel: Record<string, string> = {
    'visible': 'Visible',
    'detached': 'Detached',
    'hidden': 'Hidden',
};

/**
 * todo -
 * */
function describeNumberOfBidders(num_bidders: number) {
    if (num_bidders === 1){
        <div className="flex items-center space-x-1">
                <span>There is </span>
                <span className="text-lg font-semibold">{num_bidders}</span>
                <span>active bidder on the project</span>
            </div>
    } else {
        return (

            <div className="flex items-center space-x-1">
                <span>There are </span>
                <span className="text-lg font-semibold">{num_bidders}</span>
                <span>active bidders on the project</span>
            </div>
        );
    }
}

function Row({item}: { item: BidViewItem }) {

    if (!item.yours) {
        return (
            <div className="flex justify-between border-b py-1 ">
                <span className="text-sm">{
                    typeof item.amount === "number"
                        ? moneyFormatter.format(item.amount)
                        : item.amount
                }</span>
                <span className="text-sm">{item.name}</span>
            </div>
        );
    } else {
        return (
            <div className="flex justify-between border-b py-1 bg-blue-100 rounded-md space-x-20">
                <span className="font-semibold text-md">{
                    typeof item.amount === "number"
                        ? moneyFormatter.format(item.amount)
                        : item.amount
                }</span>
                <span className="font-semibold text-md">{item.name}</span>
            </div>
        );
    }
}

/**
 * These are the 2 modes of the card
 * 1) LineItemCard
 * 2) TextCard
 * */
function LineItemCard({bidViewData}: BidFeedbackProps) {
    return (
        <div className="space-y-2">
            {bidViewData?.items?.map((item, index) =>
                <Row key={index} item={item}/>)}

            {bidViewData?.names ? (
                <div className="text-sm text-gray-600">
                    <div className="font-medium">The other bidders are:</div>
                    <div className="space-x-2 space-y-1">
                        {bidViewData.names.map((name) => (
                            <Badge key={name}>{name}</Badge>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function TitleLink({bidViewData}: BidFeedbackProps){
    return <div>
                Your Latest Bid:{" "}
                <Link
                    to={`/project/${bidViewData.latest?.project_id}/bid/${bidViewData.latest?.id}`}
                >
                    <span className="text-lg font-semibold">
                        {moneyFormatter.format(
                            bidViewData?.latest?.bid_amount
                        )}
                    </span>
                </Link>
            </div>
}


function TextCard({bidViewData, showTitle=true}: BidFeedbackProps) {
    return (
        <div className="space-y-2">
            {showTitle &&
                <TitleLink bidViewData={bidViewData} />
            }
            {describeNumberOfBidders(bidViewData?.num_bidders)}

            {bidViewData?.ranking ? (
                <div >
                    <span>The Current Bid is ranked {" "}</span>
                    <span className="text-lg font-semibold">{bidViewData.ranking}{getOrdinal(bidViewData.ranking)}</span>
                    <span>{" "} out of {" "}</span>
                    <span className="text-lg font-semibold">{bidViewData.num_bidders}</span>
                </div>
            ) : null}

            {bidViewData?.names ? (
                <div>
                    <div className="text-sm text-gray-600 font-medium">
                        The other bidders are:
                    </div>
                    <div className="space-x-2 space-y-1">
                        {bidViewData.names.map((name) => (
                            <Badge key={name}>{name}</Badge>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}



/**
 * Generate fake data based for a card so a developer can see how bids will display.
 *                  amounts
 *              vv | vr | vh    v-lmh
 * names        hv | hr | hh    h-lmh
 *              dv | dr | dh    d-lmh
 *
 * names: visible, hidden, detached
 * amounts: visible, ranked, hidden, low-medium,high
 *
 * */
export function SampleData(mode: BidderViewMode) {
    const res: BiddingView = {
        mode: mode,
        num_bidders: 5,
        ranking: null,
        items: null,
        names: null,
        latest: {
            id: "",
            project_id: "",
            company_id: "",
            title: "sample",
            bid_amount: 40000000,
        },
    };
    let items = [
        {name: "Builder 1", amount: 37000000, yours: false},
        {name: "Builder 2", amount: 41500000, yours: false},
        {name: "Builder 3", amount: 42000000, yours: true},
        {name: "Builder 4", amount: 42500000, yours: false},
        {name: "Builder 5", amount: 45000000, yours: false},
    ];

    // (vv, hv, dv) -> line-item-y
    if (mode.amounts === AmountViewMode.Visible) {
        if (mode.names === NameViewMode.Visible) {
            res.items = items;
        } else {
            res.items = items.map((x) =>
                x.yours ? x : {...x, ...{name: "redacted"}}
            );
        }
    }

    // (vr, hr, dr) -> add the ranking
    if (mode.amounts === AmountViewMode.Ranked) {
        res.ranking = 3;

        // (vr) -> special case
        if (mode.names === NameViewMode.Visible) {
            res.items = items.map((x) =>
                x.yours ? x : {...x, ...{amount: "redacted"}}
            );
        }
    }

    // diagram mode
    if (mode.amounts === AmountViewMode.Diagram) {
        res.diagram = [
            {position: 0.0, yours: false, name: mode.names === NameViewMode.Visible ? "Builder 1" : null},
            {position: 0.45, yours: false, name: mode.names === NameViewMode.Visible ? "Builder 2" : null},
            {position: 0.5, yours: true, name: mode.names === NameViewMode.Visible ? "Your Company" : null},
            {position: 0.65, yours: false, name: mode.names === NameViewMode.Visible ? "Builder 4" : null},
            {position: 1.0, yours: false, name: mode.names === NameViewMode.Visible ? "Builder 5" : null},
        ];
        res.ranking = 3;
    }

    // (dv, dr, dh, vh) -> attach the list of competitors
    if (
        mode.names === NameViewMode.Detached ||
        (mode.names === NameViewMode.Visible &&
            mode.amounts === AmountViewMode.Hidden)
    ) {
        res.names = items.filter((x) => !x["yours"]).map((x) => x["name"]);
    }
    return res;
}


type FeedbackCardProps = {
    bidViewData: BiddingView
    showTitle?: boolean
}

export const BuilderFeedbackCard = ({bidViewData, showTitle=true}: FeedbackCardProps) => {
    /**
     * See BidConfig.jpg for the logic
     * essentially there are 2 modes - 'line-item-y' or 'text-card-y'
     *
     * Only the data that is available to a bidder is returned.
     * */

    if (!bidViewData.latest) {
        return <div>Submit a bid to see where you rank.</div>;
    } else if (bidViewData?.mode.amounts === AmountViewMode.Diagram) {
        return <WhereYouStandDiagram bidViewData={bidViewData} showTitle={showTitle}/>;
    } else if (bidViewData?.mode.amounts === AmountViewMode.Visible) {
        return <LineItemCard bidViewData={bidViewData}/>;
    } else if (bidViewData?.mode.amounts === AmountViewMode.Ranked) {
        if (bidViewData?.mode.names === NameViewMode.Visible) {
            return <LineItemCard bidViewData={bidViewData}/>;
        } else {
            return <TextCard bidViewData={bidViewData} showTitle={showTitle}/>;
        }
    } else {
        return <TextCard bidViewData={bidViewData} showTitle={showTitle}/>;
    }
}


export const AnnotatedFeedbackCard = ({bidViewData}: FeedbackCardProps) => {
    const {project_id} = useParams<{ project_id:string }>();
    // console.log(bidViewData)
    return <div>
        {bidViewData.scope
            ? <div className="text-lg font-semibold flex items-center justify-between w-full">
                <Link
                    className=""
                    to={`/project/${project_id}/scopes/${bidViewData.scope.id}`}
                    >Scope: {bidViewData.scope.title}
                </Link>
                <CreateBidButton scope_id_arg={bidViewData.scope.id}/>
              </div>
            : <></>
        }
        <BuilderFeedbackCard bidViewData={bidViewData} />
    </div>
}


type FeedbackProps = {
    bidViewData: BiddingView | undefined
    round: RoundModel
}


/**
 *
 * */
const AuctionStatusView = ({bidViewData, round}: FeedbackProps) => {
    const {accessToken} = useAuthInfo();
    const {project_id, scope_id} = useParams<{ project_id:string, scope_id:string }>();
    const [feedback, setFeedback] = useState< BiddingView[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        const fetchData = async () => {
            if (accessToken) {
                let url;
                if(scope_id) {
                    url = `/projects/${project_id}/feedback/?scope_id=${scope_id}`
                } else {
                    url = `/projects/${project_id}/feedback/`
                }

                const response = await fetchWithToken(url, accessToken);
                // console.log(response);
                setFeedback(response);
            }
        }
        fetchData()
    }, [project_id, scope_id]);

    if (!feedback) {
        return <div><BaseLoadingSpinner/></div>;
    }
    return (
        <Card>
            <CardHeader>
                <div className="flex justify-between items-center">
                    <CardTitle>Bidding Summary</CardTitle>
                    {/*<Button variant="outline">*/}
                    {/*            /!*<Link*!/*/}
                    {/*             to={`/project/${project_id}/edit`}*!/*/}
                    {/*            /!*></Link>*!/*/}
                    {/*    More Info*/}
                    {/*</Button>*/}
                </div>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
                <ul className="space-y-1">
                    {/* column 1 -> infor on bid setup */}
                    <li>
                        <span>Current Round: {round.name} </span>
                        {round.end_date
                            ? <span>will last until
                                    <strong> {round.end_date.split("T")[0]}</strong>
                                  </span>
                            : <span>is open indefinitely</span>
                        }
                    </li>
                    <li>
                        <strong>{round.eligibility === Eligibility.Closed
                            ? "The Project is not accepting Bids at this time"
                            : "The project is accepting Bids"}
                        </strong>
                    </li>
                    <li>
                        <span>Competing bidder amounts are: </span>
                        <span className="font-medium">
                            {AmountViewLabel[round.bid_config.bid_view_mode.amounts] || round.bid_config.bid_view_mode.amounts}
                        </span>
                    </li>
                    <li>
                        <span>Bidder names are: </span>
                        <span className="font-medium">
                            {NameViewLabel[round.bid_config.bid_view_mode.names] || round.bid_config.bid_view_mode.names}
                        </span>
                    </li>
                </ul>
                {feedback
                    ? feedback.map((x) => <AnnotatedFeedbackCard bidViewData={x}/>)
                    : <></>
                }
            </CardContent>
        </Card>
    );
};

export default AuctionStatusView;
